import React from 'react';
import { Row, Col } from 'reactstrap';
import { Card, Avatar, Collapse, List, Icon, Affix, Divider } from 'antd';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import SidebarButtons from './sidebar-buttons';
import { ExpiredTooltip } from 'components/UIKit';

import messages from './messages';

import 'antComponentsStyle';

const CardTitle = ({ proposalId, isExpired }) =>
  proposalId ? (
    isExpired ? (
      <ExpiredTooltip showIcon>
        Preventivo <span className="d-lg-inline d-md-none">numero</span> #{proposalId.toString().padStart(5, '0')}
      </ExpiredTooltip>
    ) : (
      <>Preventivo <span className="d-lg-inline d-md-none">numero</span> #{proposalId.toString().padStart(5, '0')}</>
    )
  ) : (
    <></>
  );

const SidebarPreview = ({ proposal, price: offerPrice, currentStep, printAllowed, feeAllowed }) => {
  if(!proposal.quotation) {
    return <></>;
  }

  let { car, quotation, selectedServices, equippedOptionals } = proposal;
  quotation = quotation && quotation.data ? quotation.data : {};

  if (!car) {
    car = quotation.offer.data.car
  }

  const price = proposal.price || offerPrice;
  const deposit = quotation.offer && quotation.offer.data ? quotation.offer.data.deposit : proposal.deposit;
  const duration = quotation.offer && quotation.offer.data ? quotation.offer.data.duration : proposal.duration;
  const distance = quotation.offer && quotation.offer.data ? quotation.offer.data.distance : proposal.distance;
  const bundle = quotation.offer && quotation.offer.data ? quotation.offer.data.bundle : proposal.bundle;

  const car_features = [
    {
      label: 'Alimentazione',
      description: car.fuel.name,
    },
    {
      label: 'Cambio',
      description: car.descrizione_cambio,
    },
    {
      label: 'Cilindrata',
      description: `${car.cilindrata} cc`,
    },
    {
      label: 'Posti',
      description: `${car.posti}`,
    },
    {
      label: 'Porte',
      description: `${car.porte}`,
    },
    {
      label: 'Dimensioni',
      description: `${car.larghezza} cm x ${car.lunghezza} cm x ${
        car.altezza
      } cm`,
    },
    {
      label: 'Capacità bagagliaio',
      description: `${car.bagagliaio} l`,
    },
    {
      label: 'Potenza',
      description: `${car.hp} CV / ${car.kw} kW`,
    },
    {
      label: 'Trazione',
      description: `${car.descrizione_trazione}`,
    },
    {
      label: 'Consumo medio',
      description: `${car.consumo_medio} (L/100 Km)`,
    },
    {
      label: 'Emissioni di CO2',
      description: `${car.emissioni_co2 ? car.emissioni_co2 : '-'}  g/Km`,
    },
  ];

  const quotation_features = [
    {
      label: 'Rata mensile',
      description: `${parseInt(price).toLocaleString('it')} €`
    },
    {
      label: 'Anticipo',
      description: `${parseInt(deposit).toLocaleString('it')} €`
    },
    {
      label: 'Durata',
      description: `${duration} mesi`,
    },
    {
      label: 'Distanza',
      description: `${parseInt(distance).toLocaleString('it')} Km/anno`
    },
  ];

  if (bundle && bundle !== '') {
    quotation_features.push({
      label: 'Pacchetto',
      description: bundle,
    });
  }

  const { stillValid } = proposal;
  const isExpired = stillValid === false && currentStep < 3;

  const color = proposal.offer_color || (proposal.offer && proposal.offer.color) || null;
  let carImage = null;

  if (proposal.offer && proposal.offer.data) {
    carImage = proposal.offer.data.image;
  } else if (quotation.offer.data.images && quotation.offer.data.images.data.length) {
    carImage = quotation.offer.data.images.data[0].path;
  }

  let quotationFee = `Guadagno potenziale: `;

  if (proposal.offer && proposal.offer && proposal.offer.data) {
    const originalCommissionToShow = Math.round(parseFloat(proposal.original_commission)).toLocaleString('it');
    const commissionToShow = Math.max(0, Math.round(parseFloat(proposal.commission))).toLocaleString('it');

    if (proposal.original_commission !== proposal.commission) {
      quotationFee += `<strike>${originalCommissionToShow}€</strike> <strong>${commissionToShow}€</strong>`
    } else {
      quotationFee += `<strong>${commissionToShow}€</strong>`
    }
  }

  return (
    <>
      <Affix>
        <div className="element-box">
          <Card
            className="d-none d-md-block d-xl-none"
            cover={<img src={carImage} alt={'Immagine '+car.descrizione_modello} />}
            bordered={false}
            bodyStyle={{padding: '8px'}}
          >
            <Card.Meta
            title={<CardTitle proposalId={proposal.id} isExpired={isExpired} />}
            description={
              <>
                <h3>{car.brand.name} {car.descrizione_modello}</h3>
                {car.allestimento}
                {color && <div className="car-color">{color}</div>}
              </>
            }
          />
          </Card>

          <Card.Meta
            className="d-none d-xl-block"
            avatar={<Avatar shape="square" size={128} src={carImage} style={{height: '85px'}} />}
            title={<CardTitle proposalId={proposal.id} isExpired={isExpired} />}
            description={
              <>
                <h3>{car.brand.name} {car.descrizione_modello}</h3>
                {car.allestimento}
                {color && <div className="car-color">{color}</div>}
              </>
            }
          />
          <Divider className="my-md-2 my-lg-4" />

          <Row id="quotation-action-buttons" className="no-gutters mt-xl-4 ant-row-flex ant-row-flex-middle ant-row-flex-center">
            <Col xl={6} lg={printAllowed ? 5 : 12} md={12} className="mb-2">
              <Row id="quotation-cost-recap" className="no-gutters ant-row-flex ant-row-flex-middle ant-row-flex-center">
                <Col xs={6} lg={printAllowed ? 11 : 6} md={6} xl={6} className="text-right">
                  <div id="quotation-cost-price">
                    {parseInt(price).toLocaleString('it')}€
                  </div>
                  <div className={classnames({
                    'price-description d-none d-md-none d-xl-none': true,
                    'd-lg-block': printAllowed
                  })}>
                    <strong>Canone mensile</strong>
                    <br />
                    {messages.vat.defaultMessage}
                  </div>
                </Col>
                <Col xs={6} xl={6} className={classnames({
                  'text-left pl-2 d-xl-block': true,
                  'd-lg-none': !!printAllowed
                })}>
                  <div id="quotation-cost-title">
                    Canone mensile
                  </div>
                  <div id="quotation-cost-periodicity">
                    {messages.vat.defaultMessage}
                  </div>
                </Col>
              </Row>
            </Col>
            {printAllowed &&
            <Col xl={6} lg={7} md={12} className="ant-row-flex ant-row-flex-middle ant-row-flex-center text-center pl-lg-2">
              <SidebarButtons proposal={proposal} stillValid={stillValid} currentStep={currentStep} />
            </Col>
            }
          </Row>

          <Row>
            <Col md={24} className="detail-terms mt-3 text-left">
              <Icon type='info-circle' className="info-icon-span" />
              Il prezzo del canone mensile è soggetto a variazioni.<br />
              Offerta economica limitata valida fino ad esaurimento scorte.
            </Col>
          </Row>
        </div>
      </Affix>

      <div className="element-box">
        <Collapse expandIconPosition="right" bordered={false}>
          <Collapse.Panel
          header={<FormattedMessage {...messages.sidebar_recap_title_1} values={{car_title: `${car.brand.name} ${car.descrizione_modello}`}} />}
          key="1">
            <List
              size="small"
              className="car-features"
              dataSource={car_features}
              renderItem={feature => (
                <List.Item>
                  <List.Item.Meta
                    title={feature.label}
                    description={feature.description}
                  />
                </List.Item>
              )}
            />
          </Collapse.Panel>
          <Collapse.Panel header={<FormattedMessage {...messages.sidebar_recap_title_2} />} key="2">
            <List
              size="small"
              className="quotation-features"
              dataSource={quotation_features}
              renderItem={feature => (
                <List.Item>
                  <List.Item.Meta
                    title={feature.label}
                    description={feature.description}
                  />
                </List.Item>
              )}
            />
          </Collapse.Panel>
          {selectedServices && selectedServices.length ?
            <Collapse.Panel header={<FormattedMessage {...messages.sidebar_recap_title_3} />} key="3">
              <List
                size="small"
                className="quotation-features"
                dataSource={selectedServices}
                renderItem={service => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${
                        service.min_quantity !== service.max_quantity && service.quantity > 1 ?
                        `${service.name} (x ${service.quantity})`
                        :
                        service.name
                      }`}
                      description={
                        <Icon type="check-circle" theme="filled" style={{color: "#2ec58d"}} />
                      }
                    />
                  </List.Item>
                )}
              />
            </Collapse.Panel>
            : null
          }
          {equippedOptionals && equippedOptionals.length > 0 ?
            <Collapse.Panel header={<FormattedMessage {...messages.equippedOptionals} />} key="4">
              <List
                size="small"
                className="quotation-features"
                dataSource={[equippedOptionals]}
                renderItem={equippedOptional => (
                  <List.Item>
                    <List.Item.Meta
                      title={equippedOptional}
                      description={
                        <Icon type="check-circle" theme="filled" style={{color: "#2ec58d"}} />
                      }
                    />
                  </List.Item>
                )}
              />
            </Collapse.Panel>
            : null
          }

          {proposal.accessories && proposal.accessories.length ?
            <Collapse.Panel header={<FormattedMessage {...messages.sidebar_recap_title_4} />} key="5">
              <List
                size="small"
                dataSource={proposal.accessories}
                renderItem={accessory => (
                  <List.Item>
                    <List.Item.Meta
                      title={accessory}
                    />
                  </List.Item>
                )}
              />
            </Collapse.Panel>
            : null
          }
          <Collapse.Panel header={<FormattedMessage {...messages.sidebar_recap_title_5} />} key="6">
            {proposal.notes && proposal.notes.length > 0 ? proposal.notes : `-`}
          </Collapse.Panel>
          {proposal.offer && feeAllowed &&
          <Collapse.Panel header={<FormattedMessage {...messages.sidebar_recap_title_7} />} key="7">
            <p dangerouslySetInnerHTML={{__html: quotationFee}} />
            <p>
              {messages.vatNotice.defaultMessage}
            <br />
            {messages.vatNoticeDescription.defaultMessage}
            </p>
          </Collapse.Panel>
          }
        </Collapse>
      </div>
    </>
  );
};

export default SidebarPreview;
